<template>
    <CustomTooltip :disable="disableTooltip" :tooltip-props="tooltipProps">
        <template #activator>
            <v-btn
                class="table-action-button"
                :aria-label="label"
                :color="color"
                :disabled="disabled"
                :href="href"
                :target="target"
                :size="size"
                :variant="variant"
                @click.stop="emit('click', $event)"
            >
                <font-awesome-icon :icon="icon" :style="iconStyle" />
            </v-btn>
        </template>

        <template #content>
            <span>{{ tooltip ?? label }}</span>
        </template>
    </CustomTooltip>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import CustomTooltip from '@/components/ui/CustomTooltip.vue';

type ButtonVariant = 'flat' | 'text' | 'elevated' | 'outlined' | 'plain' | 'tonal';
type ButtonSize = 'x-small' | 'small' | 'default' | 'large' | 'x-large';

const iconSizeByButtonSize: Record<ButtonSize, string> = {
    'x-small': '0.75rem',
    small: '0.85rem',
    default: '0.95rem',
    large: '1.05rem',
    'x-large': '1.15rem',
};

const props = withDefaults(
    defineProps<{
        icon: string;
        label: string;
        tooltip?: string;
        color?: string;
        disabled?: boolean;
        disableTooltip?: boolean;
        href?: string;
        target?: string;
        size?: ButtonSize;
        variant?: ButtonVariant;
        tooltipLocation?: 'top' | 'bottom';
    }>(),
    {
        tooltip: undefined,
        color: undefined,
        disabled: false,
        disableTooltip: false,
        href: undefined,
        target: undefined,
        size: 'x-small',
        variant: 'tonal',
        tooltipLocation: 'bottom',
    },
);

const emit = defineEmits<{
    click: [event: MouseEvent];
}>();

const tooltipProps = computed<Record<string, boolean>>(() => ({
    [props.tooltipLocation]: true,
}));

const iconStyle = computed<Record<string, string>>(() => ({
    fontSize: iconSizeByButtonSize[props.size],
}));
</script>

<style scoped>
.table-action-button {
    line-height: 1;
}
</style>
